// variant-resolver.ts

import jsonLogic, { type RulesLogic } from 'json-logic-js';
import makerVariantRules from '../variants/maker-variant-rules.json';
import makerVariants from '../variants/maker-variants.json';

type VariantRule = {
  condition: RulesLogic;
  variant: string;
};

export type Variant = {
  name: string;
  description: string;
  features: Record<string, string | number | boolean>;
};

const variantGroups = makerVariants.variants as Record<string, Variant>;
const variantSelection = makerVariantRules;

export type VariantName = keyof typeof variantGroups;

export type VariantContext = {
  query: Record<string, string>;
  hash_params: Record<string, string>;
  path: string;
  host: string;
  device: 'mobile' | 'desktop';
  browser: string;
  cookies: Record<string, string>;
  js_vars: Record<string, unknown>;
  hour: number;
};

let _currentContext: VariantContext | null = null;

const registerCustomOperations = () => {
  jsonLogic.add_operation('js', (code: unknown) => {
    if (typeof code !== 'string' || !code.trim() || !_currentContext) return false;
    try {
      const ctx = _currentContext;
      const fn = new Function(
        'path',
        'host',
        'device',
        'browser',
        'query',
        'hash_params',
        'cookies',
        'js_vars',
        'hour',
        `const __r = (${code}); return typeof __r === 'function' ? !!(__r()) : !!(__r);`
      );
      return Boolean(
        fn(
          ctx.path,
          ctx.host,
          ctx.device,
          ctx.browser,
          ctx.query,
          ctx.hash_params,
          ctx.cookies,
          ctx.js_vars,
          ctx.hour
        )
      );
    } catch {
      return false;
    }
  });

  jsonLogic.add_operation(
    'startsWith',
    (str: unknown, prefix: unknown) =>
      typeof str === 'string' && typeof prefix === 'string' && str.startsWith(prefix)
  );

  jsonLogic.add_operation(
    'doesNotStartWith',
    (str: unknown, prefix: unknown) =>
      typeof str === 'string' && typeof prefix === 'string' && !str.startsWith(prefix)
  );

  jsonLogic.add_operation(
    'endsWith',
    (str: unknown, suffix: unknown) =>
      typeof str === 'string' && typeof suffix === 'string' && str.endsWith(suffix)
  );

  jsonLogic.add_operation(
    'doesNotEndWith',
    (str: unknown, suffix: unknown) =>
      typeof str === 'string' && typeof suffix === 'string' && !str.endsWith(suffix)
  );

  jsonLogic.add_operation(
    'includes',
    (str: unknown, substr: unknown) =>
      typeof str === 'string' && typeof substr === 'string' && str.includes(substr)
  );

  jsonLogic.add_operation(
    'doesNotInclude',
    (str: unknown, substr: unknown) =>
      typeof str === 'string' && typeof substr === 'string' && !str.includes(substr)
  );

  jsonLogic.add_operation('matchRegex', (str: unknown, pattern: unknown, flags?: unknown) => {
    if (typeof str !== 'string' || typeof pattern !== 'string') return false;
    try {
      return new RegExp(pattern, typeof flags === 'string' ? flags : undefined).test(str);
    } catch {
      return false;
    }
  });
};

const detectBrowser = (): string => {
  const ua = navigator.userAgent;

  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';

  return 'Unknown';
};

const computeQuery = (): Record<string, string> => {
  const params = new URLSearchParams(window.location.search);
  const query: Record<string, string> = {};
  params.forEach((value, key) => {
    query[key] = value;
  });
  return query;
};

const computeDevice = (): 'mobile' | 'desktop' => (window.innerWidth < 768 ? 'mobile' : 'desktop');

const extractVarNames = (node: unknown, prefix: string): string[] => {
  if (!node || typeof node !== 'object') return [];
  if (Array.isArray(node)) return node.flatMap((n) => extractVarNames(n, prefix));

  const record = node as Record<string, unknown>;

  if ('var' in record && typeof record.var === 'string' && record.var.startsWith(prefix)) {
    return [record.var.slice(prefix.length)];
  }

  return Object.values(record).flatMap((v) => extractVarNames(v, prefix));
};

const resolveNestedProperty = (obj: unknown, path: string): unknown => {
  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }

  return current;
};

const computeHashParams = (): Record<string, string> => {
  const referencedNames = new Set(extractVarNames(variantSelection.rules, 'hash_params.'));
  const params = new URLSearchParams(window.location.hash.slice(1));
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    if (referencedNames.has(key)) result[key] = value;
  });
  return result;
};

const unflattenObject = (entries: [string, unknown][]): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [path, value] of entries) {
    const parts = path.split('.');
    let current = result;

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!current[part] || typeof current[part] !== 'object') {
        current[part] = {};
      }
      current = current[part] as Record<string, unknown>;
    }

    current[parts[parts.length - 1]] = value;
  }

  return result;
};

const computeJsVars = (): Record<string, unknown> => {
  const entries = extractVarNames(variantSelection.rules, 'js_vars.').map((name) => [
    name,
    resolveNestedProperty(globalThis, name)
  ]);
  return unflattenObject(entries as [string, unknown][]);
};

const computeCookies = (): Record<string, string> => {
  const referencedNames = new Set(extractVarNames(variantSelection.rules, 'cookies.'));

  try {
    return Object.fromEntries(
      document.cookie
        .split(';')
        .filter((c) => c.trim())
        .map((c) => {
          const [key, ...rest] = c.trim().split('=');
          return [key.trim(), decodeURIComponent(rest.join('='))];
        })
        .filter(([key]) => referencedNames.has(key))
    );
  } catch (err) {
    console.error('[maker-variant-resolver] Failed to parse cookies:', err);
    return {};
  }
};

export const computeContext = (): VariantContext => ({
  query: computeQuery(),
  hash_params: computeHashParams(),
  path: window.location.pathname,
  host: window.location.host,
  device: computeDevice(),
  browser: detectBrowser(),
  cookies: computeCookies(),
  js_vars: computeJsVars(),
  hour: new Date().getHours()
});

export const resolveActiveVariant = (context: VariantContext): VariantName => {
  // Preview-only override: not stored in rules JSON, never evaluated in production.
  // On preview hostnames (e.g. abc123-preview.devi.test), ?mkr_variant=<key> pins the active variant
  // so developers can manually switch between variants without editing rules.
  if (context.host.includes('-preview.')) {
    const requested = context.query['mkr_variant'];
    if (requested && requested in variantGroups) {
      return requested as VariantName;
    }
  }

  _currentContext = context;
  try {
    const matchingRule = (variantSelection.rules as VariantRule[]).find((rule) =>
      jsonLogic.apply(rule.condition, context)
    );
    return (matchingRule?.variant ?? variantSelection.default) as VariantName;
  } finally {
    _currentContext = null;
  }
};

// TODO: at build time, replace __mkrVariantGroup with __mkrVariantGroup_<projectId> to scope the global per project
type MkrGlobal = { __mkrVariantGroup?: Variant['features'] };

const getActiveGroup = (): Variant['features'] =>
  (globalThis as unknown as MkrGlobal).__mkrVariantGroup ?? {};

export const initializeMakerVariantResolver = () => {
  const context = computeContext();
  const activeVariant = resolveActiveVariant(context);
  const group = variantGroups[activeVariant];

  if (!group) {
    console.warn(
      `[maker] Variant "${activeVariant}" not found in maker-variants.json. Falling back to empty features.`
    );
  }

  (globalThis as unknown as MkrGlobal).__mkrVariantGroup = group?.features ?? {};
};

export const makerVariant = <T>(key: string, fallback?: T): T => {
  const value = getActiveGroup()[key];

  return value !== undefined ? (value as T) : (fallback as T);
};

export const makerVariantComponent = <T>(
  key: string,
  registry: Record<string, T>,
  fallback: T
): T => {
  const name = makerVariant<string>(key);

  return registry[name] ?? fallback;
};

if (!(globalThis as Record<string, unknown>)['__mkrVariantResolverInitialized']) {
  (globalThis as Record<string, unknown>)['__mkrVariantResolverInitialized'] = true;
  registerCustomOperations();
  initializeMakerVariantResolver();
}
